// TanStack Query hooks for /api/reports/*.

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface TrialBalanceRow {
  ledgerId: string;
  ledgerName: string;
  groupName: string;
  groupNature: 'ASSET' | 'LIABILITY' | 'INCOME' | 'EXPENSE' | string;
  opening: number;
  movement: number;
  closingDebit: number;
  closingCredit: number;
}

export interface TrialBalanceResponse {
  asOfDate: string;
  rows: TrialBalanceRow[];
  totals: { debit: number; credit: number; balanced: boolean; currency: string };
}

export function useTrialBalance(asOfDate?: string, includeZero = false) {
  return useQuery<TrialBalanceResponse>({
    queryKey: ['reports', 'trial-balance', asOfDate ?? 'today', includeZero],
    queryFn: async () => {
      const res = await api.get<TrialBalanceResponse>('/reports/trial-balance', {
        params: { ...(asOfDate && { asOfDate }), includeZero },
      });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export interface PnlRow { ledgerId: string; ledgerName: string; groupName: string; amount: number; }
export interface PnlResponse {
  period: { from: string; to: string };
  income: { rows: PnlRow[]; total: number };
  expense: { rows: PnlRow[]; total: number };
  netProfit: number;
  netProfitIsPositive: boolean;
  currency: string;
}

export function usePnL(fromDate?: string, toDate?: string) {
  return useQuery<PnlResponse>({
    queryKey: ['reports', 'pnl', fromDate ?? 'fy', toDate ?? 'today'],
    queryFn: async () => {
      const res = await api.get<PnlResponse>('/reports/pnl', { params: { ...(fromDate && { fromDate }), ...(toDate && { toDate }) } });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export interface BalanceSheetRow { ledgerId: string; ledgerName: string; groupName: string; amount: number; }
export interface BalanceSheetResponse {
  asOfDate: string;
  assets: { rows: BalanceSheetRow[]; total: number };
  liabilities: { rows: BalanceSheetRow[]; total: number };
  periodProfit: number;
  balancingGap: number;
  currency: string;
}

export function useBalanceSheet(asOfDate?: string) {
  return useQuery<BalanceSheetResponse>({
    queryKey: ['reports', 'balance-sheet', asOfDate ?? 'today'],
    queryFn: async () => {
      const res = await api.get<BalanceSheetResponse>('/reports/balance-sheet', { params: { ...(asOfDate && { asOfDate }) } });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export interface OutstandingRow {
  billRefId: string;
  partyId: string;
  partyName: string;
  partyType: string;
  msmeClassification: string;
  ledgerId: string;
  ledgerName: string;
  voucherId: string;
  voucherNumber: string;
  voucherDate: string;
  refName: string;
  refDate: string;
  dueDate?: string | null;
  amount: number;
  settledAmount: number;
  outstanding: number;
  ageDays: number;
  ageBucket: '0-30' | '31-60' | '61-90' | '90+';
}
export interface OutstandingResponse {
  rows: OutstandingRow[];
  totals: { total: number; bucket0_30: number; bucket31_60: number; bucket61_90: number; bucket90Plus: number };
  currency: string;
}

export interface ChaiBriefResponse {
  cashOnHand: number | null;
  todaysCollections: number | null;
  receivables: number;
  vouchersToday: number;
  alerts?: {
    msme43bh: { atRiskAmount: number; disallowedAmount: number; billCount: number };
    tallyOutbox: { failed: number; pending: number };
    cashLedgersNegative: number;
  };
  asOf?: string;
  needsCompanyPick?: boolean;
}

export function useChaiBrief() {
  return useQuery<ChaiBriefResponse>({
    queryKey: ['reports', 'chai-brief'],
    queryFn: async () => {
      const res = await api.get<ChaiBriefResponse>('/reports/chai-brief');
      return res.data;
    },
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });
}

export interface LedgerDetailRow {
  voucherId: string | null;
  voucherNumber: string;
  voucherTypeAbbr: string;
  voucherTypeName: string;
  voucherDate: string;
  partyName?: string | null;
  narration?: string | null;
  debit: number;
  credit: number;
  runningBalance: number;
}
export interface LedgerDetailResponse {
  ledger: { id: string; name: string; groupName: string; groupNature: string; billByBill: boolean };
  period: { from: string; to: string };
  opening: number;
  closing: number;
  rows: LedgerDetailRow[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

export function useLedgerDetail(id: string | null, fromDate?: string, toDate?: string) {
  return useQuery<LedgerDetailResponse>({
    queryKey: ['reports', 'ledger', id, fromDate, toDate],
    queryFn: async () => {
      const res = await api.get<LedgerDetailResponse>(`/reports/ledger/${id}`, {
        params: { ...(fromDate && { fromDate }), ...(toDate && { toDate }), pageSize: 500 },
      });
      return res.data;
    },
    enabled: Boolean(id),
  });
}

export interface StockSummaryRow {
  itemId: string;
  itemName: string;
  unit: string;
  openingQty: number;
  inQty: number;
  outQty: number;
  closingQty: number;
  closingValue: number;
}
export interface StockSummaryResponse {
  asOfDate: string;
  rows: StockSummaryRow[];
  totals: { closingValue: number };
  currency: string;
}
export interface ItemDetailRow {
  voucherId: string | null;
  voucherNumber: string;
  voucherTypeAbbr: string;
  voucherBaseTypeCode: string | null;
  voucherDate: string;
  partyName?: string | null;
  direction: 'IN' | 'OUT';
  quantity: number;
  ratePerUnit: number;
  totalValue: number;
  batchNo?: string | null;
  expiryDate?: string | null;
  runningQty: number;
  runningValue: number;
}
export interface ItemDetailResponse {
  item: { id: string; name: string; itemType: string; hsnSacCode: string | null; unit: string; groupName: string; salePrice: number; purchasePrice: number; gstRatePercent: number };
  period: { from: string; to: string };
  openingQty: number;
  openingValue: number;
  closingQty: number;
  closingValue: number;
  rows: ItemDetailRow[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}
export function useItemDetail(id: string | null, fromDate?: string, toDate?: string) {
  return useQuery<ItemDetailResponse>({
    queryKey: ['reports', 'item', id, fromDate, toDate],
    queryFn: async () => {
      const res = await api.get<ItemDetailResponse>(`/reports/item/${id}`, { params: { ...(fromDate && { fromDate }), ...(toDate && { toDate }), pageSize: 500 } });
      return res.data;
    },
    enabled: Boolean(id),
  });
}

export function useStockSummary(asOfDate?: string) {
  return useQuery<StockSummaryResponse>({
    queryKey: ['reports', 'stock-summary', asOfDate ?? 'today'],
    queryFn: async () => {
      const res = await api.get<StockSummaryResponse>('/reports/stock-summary', { params: { ...(asOfDate && { asOfDate }) } });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

type ItmDet = { rt: number; txval: number; iamt: number; camt: number; samt: number; csamt: number };
type InvLine = { num: number; itm_det: ItmDet };
type B2bInvoice = { inum: string; idt: string; val: number; pos: string; rchrg: string; inv_typ: string; itms: InvLine[] };
type B2clInvoice = { inum: string; idt: string; val: number; itms: InvLine[] };
type Note = { ntty: 'C' | 'D'; nt_num: string; nt_dt: string; rsn?: string; p_gst?: string; inum?: string; idt?: string; val: number; itms: InvLine[] };
type CdnurNote = Note & { ut: 'B2CL' | 'B2CS' | 'EXPWP' | 'EXPWOP' };

export interface Gstr1Response {
  period: { from: string; to: string };
  gstin: string | null;
  fp: string;
  summary: {
    invoiceCount: number;
    creditNoteCount: number;
    b2bCount: number;
    b2clCount: number;
    b2csRowCount: number;
    cdnrCount: number;
    cdnurCount: number;
    hsnRowCount: number;
    totalTaxable: number;
    totalTax: number;
    totalInvoiceValue: number;
  };
  b2b: Array<{ ctin: string; inv: B2bInvoice[] }>;
  b2cl: Array<{ pos: string; inv: B2clInvoice[] }>;
  b2cs: Array<{ sply_ty: string; pos: string; typ: string; rt: number; txval: number; iamt: number; camt: number; samt: number; csamt: number }>;
  cdnr: Array<{ ctin: string; nt: Note[] }>;
  cdnur: CdnurNote[];
  hsn: Array<{ num: number; hsn_sc: string; desc: string; uqc: string; qty: number; val: number; txval: number; iamt: number; camt: number; samt: number; csamt: number }>;
  currency: string;
}
export function useGstr1(fromDate?: string, toDate?: string) {
  return useQuery<Gstr1Response>({
    queryKey: ['reports', 'gstr-1', fromDate ?? 'month', toDate ?? 'today'],
    queryFn: async () => {
      const res = await api.get<Gstr1Response>('/reports/gstr-1', { params: { ...(fromDate && { fromDate }), ...(toDate && { toDate }) } });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export interface Gstr3bResponse {
  period: { from: string; to: string };
  gstin: string | null;
  fp: string;
  outwardSupplies: { taxable: number; igst: number; cgst: number; sgst: number; cess: number; vouchersCount: number };
  eligibleITC: { taxable: number; igst: number; cgst: number; sgst: number; cess: number; vouchersCount: number };
  outwardZeroRated?: { taxable: number; igst: number; cess: number };
  outwardNilRated?: { taxable: number };
  outwardNonGst?: { taxable: number };
  inwardReverseCharge?: { taxable: number; igst: number; cgst: number; sgst: number; cess: number; vouchersCount: number };
  paymentOfTax: { igst: number; cgst: number; sgst: number; cess: number; total: number };
  itcLadder?: {
    igstUsedOn: { igst: number; cgst: number; sgst: number };
    cgstUsedOn: { cgst: number; igst: number };
    sgstUsedOn: { sgst: number; igst: number };
  };
  itcCarryForward?: { igst: number; cgst: number; sgst: number; cess: number };
  currency: string;
}
export function useGstr3b(fromDate?: string, toDate?: string) {
  return useQuery<Gstr3bResponse>({
    queryKey: ['reports', 'gstr-3b', fromDate ?? 'month', toDate ?? 'today'],
    queryFn: async () => {
      const res = await api.get<Gstr3bResponse>('/reports/gstr-3b', { params: { ...(fromDate && { fromDate }), ...(toDate && { toDate }) } });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export interface GroupSummaryLedger {
  ledgerId: string;
  ledgerName: string;
  groupId: string;
  groupName: string;
  opening: number;
  debit: number;
  credit: number;
  closing: number;
}
export interface GroupSummarySubgroup {
  groupId: string;
  groupName: string;
  ledgerCount: number;
  opening: number;
  debit: number;
  credit: number;
  closing: number;
}
export interface GroupSummaryResponse {
  root: { id: string; name: string; nature: string; behavesLike: string };
  fromDate: string;
  toDate: string;
  ledgers: GroupSummaryLedger[];
  subgroups: GroupSummarySubgroup[];
  totals: { opening: number; debit: number; credit: number; closing: number };
  currency: 'INR';
}

export function useGroupSummary(groupId: string | null, fromDate: string, toDate: string) {
  return useQuery<GroupSummaryResponse>({
    queryKey: ['reports', 'group-summary', groupId, fromDate, toDate],
    queryFn: async () => {
      const res = await api.get<GroupSummaryResponse>('/reports/group-summary', {
        params: { groupId, fromDate, toDate },
      });
      return res.data;
    },
    enabled: Boolean(groupId && fromDate && toDate),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

export type RegisterBaseType = 'SALES' | 'PURCHASE' | 'CREDIT_NOTE' | 'DEBIT_NOTE';
export interface RegisterRow {
  voucherId: string;
  voucherNumber: string;
  voucherDate: string;
  voucherType: string;
  partyLedgerId: string | null;
  partyName: string;
  partyDisplayName: string;
  partyGstin: string | null;
  msmeClassification: string | null;
  branchName: string | null;
  branchGstin: string | null;
  hsnCodes: string[];
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
  total: number;
  roundOff: number;
  narration: string | null;
}
export interface RegisterResponse {
  baseType: RegisterBaseType;
  fromDate: string;
  toDate: string;
  voucherCount: number;
  rows: RegisterRow[];
  totals: { taxable: number; cgst: number; sgst: number; igst: number; cess: number; total: number };
  currency: 'INR';
}

const REGISTER_PATH: Record<RegisterBaseType, string> = {
  SALES: '/reports/sales-register',
  PURCHASE: '/reports/purchase-register',
  CREDIT_NOTE: '/reports/credit-note-register',
  DEBIT_NOTE: '/reports/debit-note-register',
};

export function useRegister(baseType: RegisterBaseType, fromDate: string, toDate: string) {
  return useQuery<RegisterResponse>({
    queryKey: ['reports', 'register', baseType, fromDate, toDate],
    queryFn: async () => {
      const res = await api.get<RegisterResponse>(REGISTER_PATH[baseType], { params: { fromDate, toDate } });
      return res.data;
    },
    enabled: Boolean(fromDate && toDate),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

export interface PartyStatementEntry {
  journalLineId: string;
  voucherId: string;
  voucherNumber: string;
  voucherDate: string;
  voucherType: string;
  baseTypeCode: string;
  narration: string;
  debit: number;
  credit: number;
  runningBalance: number;
  billRefName: string | null;
  billRefType: string | null;
}
export interface PartyStatementOpenBill {
  billRefId: string;
  voucherId: string;
  voucherNumber: string;
  type: string;
  refName: string;
  refDate: string;
  dueDate: string | null;
  amount: number;
  settledAmount: number;
  outstanding: number;
  ageDays: number;
}
export interface PartyStatementResponse {
  party: {
    id: string;
    displayName: string;
    legalName: string;
    partyType: string;
    gstin: string | null;
    pan: string | null;
    msmeClassification: 'NOT_MSME' | 'MICRO' | 'SMALL' | 'MEDIUM' | null;
    msmeUdyam: string | null;
    addresses: unknown;
    contacts: unknown;
  } | null;
  ledger: { id: string; name: string; groupName?: string; groupBehavesLike?: string };
  fromDate: string;
  toDate: string;
  openingBalance: number;
  closingBalance: number;
  totalDebit: number;
  totalCredit: number;
  entryCount: number;
  entries: PartyStatementEntry[];
  openBills: PartyStatementOpenBill[];
  openBillsTotal: number;
  currency: 'INR';
}

export function usePartyStatement(partyLedgerId: string | null, fromDate: string, toDate: string) {
  return useQuery<PartyStatementResponse>({
    queryKey: ['reports', 'party-statement', partyLedgerId, fromDate, toDate],
    queryFn: async () => {
      const res = await api.get<PartyStatementResponse>('/reports/party-statement', {
        params: { partyLedgerId, fromDate, toDate },
      });
      return res.data;
    },
    enabled: Boolean(partyLedgerId && fromDate && toDate),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

export interface ReorderRow {
  itemId: string;
  itemName: string;
  hsnSacCode: string | null;
  stockGroupName: string;
  unitCode: string;
  opening: number;
  inQty: number;
  outQty: number;
  closing: number;
  reorderLevel: number;
  reorderQty: number;
  recent90DayOut: number;
  dailyAvgConsumption: number;
  daysCover: number | null;
  avgRate: number;
  closingValue: number;
  suggestedOrderQty: number;
  status: 'out-of-stock' | 'reorder-now' | 'low' | 'ok';
}
export interface ReorderResponse {
  rows: ReorderRow[];
  totals: {
    itemCount: number;
    outOfStock: number;
    reorderNow: number;
    low: number;
    ok: number;
    totalClosingValue: number;
    suggestedSpend: number;
  };
  asOf: string;
  currency: 'INR';
}

export function useReorderReport(asOf?: string) {
  return useQuery<ReorderResponse>({
    queryKey: ['reports', 'reorder', asOf ?? 'today'],
    queryFn: async () => {
      const res = await api.get<ReorderResponse>('/reports/reorder', { params: asOf ? { asOf } : undefined });
      return res.data;
    },
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

export interface CashFlowBucketLine {
  ledgerId: string;
  ledgerName: string;
  behavesLike: string;
  inflow: number;
  outflow: number;
  net: number;
}
export interface CashFlowResponse {
  openingCash: number;
  closingCash: number;
  netChange: number;
  operating: { total: number; lines: CashFlowBucketLine[] };
  investing: { total: number; lines: CashFlowBucketLine[] };
  financing: { total: number; lines: CashFlowBucketLine[] };
  cashBankLedgers: Array<{ id: string; name: string; behavesLike: string }>;
  fromDate: string;
  toDate: string;
  currency: 'INR';
}

export function useCashFlow(fromDate: string, toDate: string) {
  return useQuery<CashFlowResponse>({
    queryKey: ['reports', 'cash-flow', fromDate, toDate],
    queryFn: async () => {
      const res = await api.get<CashFlowResponse>('/reports/cash-flow', { params: { fromDate, toDate } });
      return res.data;
    },
    enabled: Boolean(fromDate && toDate),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

export interface MsmePayablesRow {
  billRefId: string;
  partyId: string;
  partyName: string;
  msmeClassification: 'MICRO' | 'SMALL' | null;
  msmeUdyam: string | null;
  gstin: string | null;
  pan: string | null;
  voucherId: string;
  voucherNumber: string;
  voucherDate: string;
  ledgerName: string;
  refName: string;
  refDate: string;
  dueDate: string | null;
  amount: number;
  settledAmount: number;
  outstanding: number;
  ageDays: number;
  windowDays: number;
  daysPastLimit: number;
  status: 'within-limit' | 'at-risk' | 'disallowed';
}

export interface MsmePayablesResponse {
  rows: MsmePayablesRow[];
  totals: {
    total: number;
    withinLimit: number;
    atRisk: number;
    disallowed: number;
    partyCount: number;
    microCount: number;
    smallCount: number;
  };
  asOf: string;
  window: number;
  classification: 'MICRO' | 'SMALL' | 'ALL';
  currency: 'INR';
}

export function useMsmePayables(asOf?: string, hasAgreement = true, classification: 'MICRO' | 'SMALL' | 'ALL' = 'ALL') {
  return useQuery<MsmePayablesResponse>({
    queryKey: ['reports', 'msme-payables', asOf ?? 'today', hasAgreement, classification],
    queryFn: async () => {
      const params: Record<string, string> = { hasAgreement: String(hasAgreement), classification };
      if (asOf) params.asOf = asOf;
      const res = await api.get<MsmePayablesResponse>('/reports/msme-payables', { params });
      return res.data;
    },
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

export function useOutstanding(direction?: 'RECEIVABLE' | 'PAYABLE', partyLedgerId?: string | null) {
  return useQuery<OutstandingResponse>({
    queryKey: ['reports', 'outstanding', direction ?? 'all', partyLedgerId ?? 'all'],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (direction) params.direction = direction;
      if (partyLedgerId) params.partyLedgerId = partyLedgerId;
      const res = await api.get<OutstandingResponse>('/reports/outstanding', { params });
      return res.data;
    },
    enabled: !partyLedgerId || Boolean(partyLedgerId), // when fetching per-party, only enable when id is set
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}
